import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sellersApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SupplierManageModal from '../../components/SupplierManageModal/SupplierManageModal';
import SupplierEditModal from '../../components/SupplierEditModal/SupplierEditModal';
import SupplierProductsModal from '../../components/SupplierProductsModal/SupplierProductsModal';
import Pagination from '../../components/Pagination/Pagination';
import './AdminSupplierManagePage.css';

const AdminSupplierManagePage = () => {
  const navigate = useNavigate();
  
  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Action dropdown state
  const [actionDropdown, setActionDropdown] = useState({ show: false, supplierId: null });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [notesSupplier, setNotesSupplier] = useState(null);
  const [supplierNotes, setSupplierNotes] = useState('');

  // Fetch suppliers from API
  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sellersApi.getAll({ limit: 200 });
      const supplierData = Array.isArray(response) ? response : (response.sellers || []);
      setSuppliers(supplierData);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Get unique countries from suppliers
  const uniqueCountries = [...new Set(suppliers.map(s => s.country).filter(Boolean))];
  
  // Get unique categories from suppliers
  const uniqueCategories = [...new Set(suppliers.map(s => s.category || s.businessType).filter(Boolean))];

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = !searchQuery || 
      supplier.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = !selectedCountry || supplier.country?.toLowerCase() === selectedCountry.toLowerCase();
    const matchesStatus = !selectedStatus || supplier.status?.toLowerCase() === selectedStatus.toLowerCase();
    const matchesCategory = !selectedCategory || 
      supplier.category?.toLowerCase().includes(selectedCategory.toLowerCase()) || 
      supplier.businessType?.toLowerCase().includes(selectedCategory.toLowerCase());
    return matchesSearch && matchesCountry && matchesStatus && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCountry, selectedStatus, selectedCategory]);

  // Handle select item
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedSuppliers.map(s => s.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle status change
  const handleStatusChange = async (supplierId, newStatus) => {
    try {
      await sellersApi.updateStatus(supplierId, newStatus);
      fetchSuppliers();
      setActionDropdown({ show: false, supplierId: null });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete supplier
  const handleDeleteSupplier = async (supplierId) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await sellersApi.delete(supplierId);
        fetchSuppliers();
        setActionDropdown({ show: false, supplierId: null });
      } catch (err) {
        console.error('Error deleting supplier:', err);
        alert('Failed to delete supplier');
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select suppliers to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} suppliers?`)) {
      try {
        await sellersApi.bulkDelete(selectedItems);
        setSelectedItems([]);
        fetchSuppliers();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete suppliers');
      }
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (status) => {
    if (selectedItems.length === 0) {
      alert('Please select suppliers to update');
      return;
    }
    try {
      await sellersApi.bulkUpdateStatus(selectedItems, status);
      setSelectedItems([]);
      fetchSuppliers();
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update suppliers');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Handle edit supplier
  const handleEditSupplier = async (supplier) => {
    try {
      setActionDropdown({ show: false, supplierId: null });
      const fullSupplierData = await sellersApi.getById(supplier.id);
      setEditSupplier(fullSupplierData);
      setShowEditModal(true);
    } catch (err) {
      console.error('Error loading supplier for edit:', err);
      alert('Failed to load supplier details');
    }
  };

  // Handle edit modal update
  const handleEditUpdate = () => {
    fetchSuppliers();
  };

  // Handle notes modal
  const handleNotesModal = async (supplier) => {
    try {
      setActionDropdown({ show: false, supplierId: null });
      const fullSupplierData = await sellersApi.getById(supplier.id);
      setNotesSupplier(fullSupplierData);
      setSupplierNotes(fullSupplierData.companyNotes || '');
      setShowNotesModal(true);
    } catch (err) {
      console.error('Error loading supplier for notes:', err);
      setNotesSupplier(supplier);
      setSupplierNotes(supplier.companyNotes || '');
      setShowNotesModal(true);
    }
  };

  // Handle save notes
  const handleSaveNotes = async () => {
    if (!notesSupplier?.id) return;
    try {
      await sellersApi.updateProfile(notesSupplier.id, { companyNotes: supplierNotes });
      alert('Notes saved successfully!');
      setShowNotesModal(false);
      setNotesSupplier(null);
      setSupplierNotes('');
      fetchSuppliers();
    } catch (err) {
      console.error('Error saving notes:', err);
      alert('Failed to save notes');
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSupplier(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.asm-action-dropdown')) {
        setActionDropdown({ show: false, supplierId: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Get verification level
  const getVerificationLevel = (supplier) => {
    if (supplier.isVerified) return 'Verified';
    if (supplier.verificationLevel === 'inspected') return 'Inspected';
    if (supplier.verificationLevel === 'certified') return 'Certified';
    return 'Verified';
  };

  // Get membership plan
  const getMembershipPlan = (supplier) => {
    if (supplier.membershipPlan) {
      return supplier.membershipPlan.split(' ')[0];
    }
    return 'Basic';
  };

  return (
    <AdminLayout>
      <div className="admin-supplier-manage-content">
        <div className="asm-page-header">
          <div className="asm-page-title-section">
            <h1 className="asm-page-title">Supplier Manage</h1>
            <p className="asm-page-description">Oversee, approve, and organize all supplier listings across the platform</p>
          </div>
          <div className="asm-header-actions">
            {selectedItems.length > 0 && (
              <>
                <button className="asm-bulk-btn asm-bulk-active" onClick={() => handleBulkStatusUpdate('active')}>
                  Activate ({selectedItems.length})
                </button>
                <button className="asm-bulk-btn asm-bulk-hide" onClick={() => handleBulkStatusUpdate('hidden')}>
                  Hide ({selectedItems.length})
                </button>
                <button className="asm-bulk-btn asm-bulk-delete" onClick={handleBulkDelete}>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>

        {/* Supplier Lists Header - Outside white content area */}
        <div className="asm-list-header">
          <h2 className="asm-list-title">Supplier Lists</h2>
          <div className="asm-list-controls">
            <div className="asm-search-box">
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            
            {/* Country Filter - Native Select */}
            <div className="asm-select-wrapper">
              <select 
                className="asm-filter-select"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Country</option>
                {uniqueCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            
            {/* Status Filter - Native Select */}
            <div className="asm-select-wrapper">
              <select 
                className="asm-filter-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="hidden">Hidden</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            
            {/* Filter By Category - Native Select */}
            <div className="asm-select-wrapper asm-category-select">
              <svg className="asm-filter-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
              </svg>
              <select 
                className="asm-filter-select with-icon"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Filter By Category</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Distributor">Distributor</option>
                <option value="Supplier">Supplier</option>
                <option value="Trading Company">Trading Company</option>
                <option value="Contractor">Contractor</option>
                {uniqueCategories.filter(cat => !['Manufacturer', 'Distributor', 'Supplier', 'Trading Company', 'Contractor'].includes(cat)).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* White Content Area - Only contains table */}
        <div className="asm-content">
          {loading ? (
            <div className="asm-loading">Loading suppliers...</div>
          ) : error ? (
            <div className="asm-error">{error} <button onClick={fetchSuppliers}>Retry</button></div>
          ) : (
          <div className="asm-table">
            <div className="asm-table-header">
              <div className="asm-th asm-th-checkbox">
                <input 
                  type="checkbox" 
                  checked={paginatedSuppliers.length > 0 && selectedItems.length === paginatedSuppliers.length}
                  onChange={handleSelectAll}
                />
                <span>All</span>
              </div>
              <div className="asm-th">Logo</div>
              <div className="asm-th">Creation date</div>
              <div className="asm-th">Company Name</div>
              <div className="asm-th">Company Type</div>
              <div className="asm-th">Location</div>
              <div className="asm-th">Verification</div>
              <div className="asm-th">Gmail</div>
              <div className="asm-th">Products</div>
              <div className="asm-th">Membership</div>
              <div className="asm-th">Groups</div>
              <div className="asm-th">Details</div>
              <div className="asm-th">Action</div>
            </div>
            <div className="asm-table-body">
              {paginatedSuppliers.length > 0 ? (
                paginatedSuppliers.map((supplier) => (
                  <div key={supplier.id} className="asm-table-row">
                    <div className="asm-td asm-td-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedItems.includes(supplier.id)}
                        onChange={() => handleSelectItem(supplier.id)}
                      />
                    </div>
                    <div className="asm-td asm-td-image">
                      {supplier.logo ? (
                        <img 
                          src={supplier.logo} 
                          alt={supplier.name}
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/50x50?text=Logo'; }}
                        />
                      ) : (
                        <div className="asm-logo-placeholder">
                          {supplier.name?.charAt(0) || 'S'}
                        </div>
                      )}
                    </div>
                    <div className="asm-td asm-td-date" data-label="Date">{formatDate(supplier.createdAt)}</div>
                    <div className="asm-td asm-td-name" title={supplier.name || 'N/A'}>{supplier.name || 'N/A'}</div>
                    <div className="asm-td asm-td-company" data-label="Type">{supplier.businessType || 'Manufacturer'}</div>
                    <div className="asm-td asm-td-location" data-label="Location">{supplier.country || supplier.city || 'N/A'}</div>
                    <div className="asm-td asm-td-verification" data-label="Verification">
                      <span className={`asm-verification-badge ${supplier.isVerified ? 'verified' : 'not-verified'}`}>
                        {getVerificationLevel(supplier)}
                      </span>
                    </div>
                    <div className="asm-td asm-td-gmail" data-label="Gmail">{supplier.email || 'N/A'}</div>
                    <div className="asm-td asm-td-products" data-label="Products">{supplier._count?.products || 0}</div>
                    <div className="asm-td asm-td-membership" data-label="Membership">{getMembershipPlan(supplier)}</div>
                    <div className="asm-td asm-td-groups" data-label="Groups">
                      <span className={`asm-status-badge ${(supplier.status || 'pending').toLowerCase()}`}>
                        {supplier.status ? supplier.status.charAt(0).toUpperCase() + supplier.status.slice(1) : 'Pending'}
                      </span>
                    </div>
                    <div className="asm-td asm-td-details">
                      <button className="asm-details-btn" onClick={() => navigate(`/admin/suppliers/${supplier.id}`)}>Details</button>
                    </div>
                    <div className="asm-td asm-td-action">
                      <div className="asm-action-dropdown">
                        <button 
                          className="asm-more-btn"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setActionDropdown({ 
                              show: actionDropdown.supplierId === supplier.id ? !actionDropdown.show : true, 
                              supplierId: supplier.id 
                            }); 
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="5" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="19" r="2" fill="#6D6D6D"/>
                          </svg>
                        </button>
                        {actionDropdown.show && actionDropdown.supplierId === supplier.id && (
                          <div className="asm-action-menu">
                            <div className="asm-action-item" onClick={() => navigate(`/seller/${supplier.slug || supplier.id}`)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#333" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="#333" strokeWidth="2"/></svg>
                              View
                            </div>
                            <div className="asm-action-item" onClick={() => handleEditSupplier(supplier)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Edit
                            </div>
                            {supplier.status?.toLowerCase() !== 'active' && (
                              <div className="asm-action-item" onClick={() => handleStatusChange(supplier.id, 'active')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Approve
                              </div>
                            )}
                            {supplier.status?.toLowerCase() !== 'hidden' && (
                              <div className="asm-action-item" onClick={() => handleStatusChange(supplier.id, 'hidden')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Hide
                              </div>
                            )}
                            {supplier.status?.toLowerCase() !== 'pending' && (
                              <div className="asm-action-item" onClick={() => handleStatusChange(supplier.id, 'pending')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/><polyline points="12 6 12 12 16 14" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Set Pending
                              </div>
                            )}
                            <div className="asm-action-item" onClick={() => handleNotesModal(supplier)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14 2 14 8 20 8" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Notes
                            </div>
                            {supplier.status?.toLowerCase() !== 'suspended' && (
                              <div className="asm-action-item asm-action-suspend" onClick={() => handleStatusChange(supplier.id, 'suspended')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f97316" strokeWidth="2"/><line x1="4" y1="4" x2="20" y2="20" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/></svg>
                                Suspend
                              </div>
                            )}
                            <div className="asm-action-item asm-action-delete" onClick={() => handleDeleteSupplier(supplier.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="asm-no-data">No suppliers found</div>
              )}
            </div>
          </div>
          )}

          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Supplier Details Modal */}
      <SupplierManageModal 
        isOpen={showModal} 
        onClose={handleCloseModal} 
        supplier={selectedSupplier}
        loading={loadingSupplier}
        onViewProducts={() => {
          setShowModal(false);
          setShowProductsModal(true);
        }}
      />

      {/* Supplier Products Modal */}
      <SupplierProductsModal
        isOpen={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        supplier={selectedSupplier}
      />

      {/* Supplier Edit Modal */}
      <SupplierEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        supplier={editSupplier}
        onUpdate={handleEditUpdate}
      />

      {/* Supplier Notes Modal */}
      {showNotesModal && (
        <div className="asm-notes-modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="asm-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="asm-notes-modal-header">
              <h3>Notes for {notesSupplier?.name || 'Supplier'}</h3>
              <button className="asm-notes-close-btn" onClick={() => setShowNotesModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="asm-notes-modal-body">
              <textarea
                className="asm-notes-textarea"
                value={supplierNotes}
                onChange={(e) => setSupplierNotes(e.target.value)}
                placeholder="Enter notes about this supplier..."
                rows={6}
              />
            </div>
            <div className="asm-notes-modal-footer">
              <button className="asm-notes-cancel-btn" onClick={() => setShowNotesModal(false)}>Cancel</button>
              <button className="asm-notes-save-btn" onClick={handleSaveNotes}>Save Notes</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSupplierManagePage;
